import { logger } from '@/utils/logger'

interface AuthSyncMessage {
  type: 'AUTH_STATE_CHANGE' | 'TOKEN_REFRESH' | 'LOGOUT'
  timestamp: number
  data?: {
    hasUser: boolean
    userEmail?: string
    reason?: string
  }
}

class CrossTabAuthSync {
  private readonly STORAGE_KEY = 'auth_sync_state'
  private readonly CHANNEL_NAME = 'auth_sync_channel'
  private readonly MESSAGE_TIMEOUT = 5000 // 5秒でメッセージを破棄

  private broadcastChannel: BroadcastChannel | null = null
  private storageListener: ((event: StorageEvent) => void) | null = null
  private messageCallbacks: Array<(message: AuthSyncMessage) => void> = []

  constructor() {
    this.initializeCommunication()
  }

  private initializeCommunication() {
    try {
      // BroadcastChannel (Chrome, Firefox)
      if (typeof BroadcastChannel !== 'undefined') {
        this.broadcastChannel = new BroadcastChannel(this.CHANNEL_NAME)
        this.broadcastChannel.onmessage = event => {
          this.handleMessage(event.data)
        }
        logger.debug('BroadcastChannel initialized for cross-tab auth sync')
      } else {
        // Fallback: localStorage events (Safari, older browsers)
        this.storageListener = (event: StorageEvent) => {
          if (event.key === this.STORAGE_KEY && event.newValue) {
            try {
              const message: AuthSyncMessage = JSON.parse(event.newValue)
              this.handleMessage(message)
            } catch (error) {
              logger.warn('Failed to parse auth sync message from localStorage', { error })
            }
          }
        }
        window.addEventListener('storage', this.storageListener)
        logger.debug('localStorage events initialized for cross-tab auth sync')
      }
    } catch (error) {
      logger.error('Failed to initialize cross-tab communication', { error })
    }
  }

  private handleMessage(message: AuthSyncMessage) {
    // 古いメッセージは無視
    if (Date.now() - message.timestamp > this.MESSAGE_TIMEOUT) {
      return
    }

    logger.debug('Received cross-tab auth sync message:', {
      type: message.type,
      timestamp: message.timestamp,
      data: message.data,
    })

    // Call all registered callbacks
    this.messageCallbacks.forEach(callback => {
      try {
        callback(message)
      } catch (error) {
        logger.error('Error in auth sync message callback:', { error })
      }
    })
  }

  sendAuthStateChange(hasUser: boolean, userEmail?: string, reason?: string) {
    const message: AuthSyncMessage = {
      type: 'AUTH_STATE_CHANGE',
      timestamp: Date.now(),
      data: {
        hasUser,
        userEmail,
        reason,
      },
    }

    this.sendMessage(message)
  }

  sendTokenRefresh(userEmail?: string) {
    const message: AuthSyncMessage = {
      type: 'TOKEN_REFRESH',
      timestamp: Date.now(),
      data: {
        hasUser: true,
        userEmail,
        reason: 'token_refreshed',
      },
    }

    this.sendMessage(message)
  }

  sendLogout(reason?: string) {
    const message: AuthSyncMessage = {
      type: 'LOGOUT',
      timestamp: Date.now(),
      data: {
        hasUser: false,
        reason,
      },
    }

    this.sendMessage(message)
  }

  private sendMessage(message: AuthSyncMessage) {
    try {
      if (this.broadcastChannel) {
        this.broadcastChannel.postMessage(message)
        logger.debug('Auth sync message sent via BroadcastChannel:', message.type)
      } else {
        // Fallback: localStorage
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(message))
        // 即座に削除してイベントトリガーのみに使用
        setTimeout(() => {
          localStorage.removeItem(this.STORAGE_KEY)
        }, 100)
        logger.debug('Auth sync message sent via localStorage:', message.type)
      }
    } catch (error) {
      logger.error('Failed to send cross-tab auth sync message', {
        error,
        messageType: message.type,
      })
    }
  }

  onMessage(callback: (message: AuthSyncMessage) => void) {
    this.messageCallbacks.push(callback)

    // Return unsubscribe function
    return () => {
      const index = this.messageCallbacks.indexOf(callback)
      if (index !== -1) {
        this.messageCallbacks.splice(index, 1)
      }
    }
  }

  destroy() {
    try {
      if (this.broadcastChannel) {
        this.broadcastChannel.close()
        this.broadcastChannel = null
      }

      if (this.storageListener) {
        window.removeEventListener('storage', this.storageListener)
        this.storageListener = null
      }

      logger.debug('Cross-tab auth sync destroyed')
    } catch (error) {
      logger.error('Failed to destroy cross-tab auth sync', { error })
    }
  }
}

export const crossTabAuthSync = new CrossTabAuthSync()
export type { AuthSyncMessage }
